import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/TranslationContext";
import { FiUser, FiMail, FiPhone, FiBriefcase, FiCalendar, FiSettings, FiLock, FiEdit3, FiSave, FiX } from "react-icons/fi";
import AnimatedModal from "./AnimatedModal";
import { authAPI, api } from "../utils/api";

function formatMegabytes(mb) {
  if (mb == null || isNaN(mb)) return "0 MB";
  if (mb >= 1024) {
    const gb = mb / 1024;
    return `${gb.toFixed(2)} GB`;
  }
  return `${Number(mb).toFixed(1)} MB`;
}

function SettingsModal({ isOpen, onClose }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Profile editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phone: user?.phoneNumber || "",
    companyName: user?.company?.name || "",
    companyType: user?.company?.companyType || "",
    joinDate: user?.trialStartDate || user?.createdAt || new Date().toISOString(),
    timezone: user?.timezone || "Pacific Standard Time (PST)"
  });

  // Update profile data when user changes
  useEffect(() => {
    if (user) {
      console.log('🔍 User object in SettingsModal:', user);
      console.log('🔍 Available user fields:', Object.keys(user));
      
      setEditedProfile({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phoneNumber || "",
        companyName: user.company?.name || "",
        companyType: user.company?.companyType || "",
        joinDate: user.trialStartDate || user.createdAt || new Date().toISOString(),
        timezone: user.timezone || "Pacific Standard Time (PST)"
      });
    }
  }, [user]);

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  // Quick stats state
  const [documentCount, setDocumentCount] = useState(0);
  const [storageUsed, setStorageUsed] = useState("0 MB");
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [sessionCount, setSessionCount] = useState(0);
  const [totalChats, setTotalChats] = useState(0);
  const [lastLogin, setLastLogin] = useState("Today");

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      setIsLoadingStats(true);
      try {
        const res = await api.get('/api/stats/quick');
        const stats = res?.stats || {};
        setDocumentCount(Number(stats.documentsUploaded || 0));
        setSessionCount(Number(stats.sessionsCreated || 0));
        setTotalChats(Number(stats.totalQuestions || 0));
        const mb = stats.storage?.megabytes ?? (stats.storage?.bytes ? stats.storage.bytes / (1024 * 1024) : 0);
        setStorageUsed(formatMegabytes(mb));

        const lastLoginDate = user?.lastLogin || localStorage.getItem('lastLogin');
        if (lastLoginDate) {
          const d = new Date(lastLoginDate);
          setLastLogin(d.toLocaleDateString());
        }
      } catch (e) {
        // Fallback: leave zeroed values on error
        console.error('Failed to load quick stats:', e);
      } finally {
        setIsLoadingStats(false);
      }
    })();
  }, [isOpen, user]);

  const handleProfileSave = () => {
    // Here you would typically call an API to update user profile
    console.log("Saving profile:", editedProfile);
    setIsEditing(false);
    // Show success message
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("Passwords do not match!");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters long!");
      return;
    }

    setIsChangingPassword(true);
    setPasswordError("");
    setPasswordSuccess("");

    try {
      await authAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword
      });
      
      setPasswordSuccess("Password updated successfully!");
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setShowPasswordSection(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setPasswordSuccess("");
      }, 3000);
    } catch (error) {
      console.error("Password change failed:", error);
      if (error.data && error.data.message) {
        setPasswordError(error.data.message);
      } else if (error.message) {
        setPasswordError(error.message);
      } else {
        setPasswordError("Failed to update password. Please try again.");
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <AnimatedModal isOpen={isOpen} onClose={onClose} className="w-full max-w-4xl max-h-[90vh]">
      <div className="bg-gradient-to-br from-white via-gray-50/50 to-indigo-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm relative overflow-hidden">
        {/* Header with gradient background */}
        <div className="relative border-b border-gray-200 dark:border-gray-700 px-8 py-6">
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center">
                  <FiUser className="w-8 h-8 text-gray-700 dark:text-gray-300" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('nav.settings')}</h2>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">{t('profile.manageProfile')}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="btn-icon"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto modal-scrollable max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Profile Information */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Profile Header */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-600/50">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <FiUser className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    {t('profile.profileInformation')}
                  </h3>
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-200 ${
                      isEditing 
                        ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400" 
                        : "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400"
                    }`}
                  >
                    {isEditing ? <FiSave className="w-4 h-4" /> : <FiEdit3 className="w-4 h-4" />}
                    {isEditing ? t('profile.save') : t('profile.edit')}
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* First Name */}
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">
                      {t('forms.firstName')}
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedProfile.firstName}
                        onChange={(e) => setEditedProfile({...editedProfile, firstName: e.target.value})}
                        className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                      />
                    ) : (
                      <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-xl text-gray-800 dark:text-gray-100">
                        {editedProfile.firstName}
                      </div>
                    )}
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">
                      {t('forms.lastName')}
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedProfile.lastName}
                        onChange={(e) => setEditedProfile({...editedProfile, lastName: e.target.value})}
                        className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                      />
                    ) : (
                      <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-xl text-gray-800 dark:text-gray-100">
                        {editedProfile.lastName}
                      </div>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block flex items-center gap-2">
                      <FiMail className="w-4 h-4" />
                      {t('forms.email')}
                    </label>
                    <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-xl text-gray-800 dark:text-gray-100">
                      {editedProfile.email}
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">({t('profile.cannotBeChanged')})</span>
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block flex items-center gap-2">
                      <FiPhone className="w-4 h-4" />
                      {t('profile.phoneNumber')}
                    </label>
                    {isEditing ? (
                      <input
                        type="tel"
                        value={editedProfile.phone}
                        onChange={(e) => setEditedProfile({...editedProfile, phone: e.target.value})}
                        className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                      />
                    ) : (
                      <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-xl text-gray-800 dark:text-gray-100">
                        {editedProfile.phone}
                      </div>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-6 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleProfileSave}
                      className="btn-primary"
                    >
                      <FiSave className="w-4 h-4" />
                      Save Changes
                    </button>
                  </div>
                )}
              </div>

              {/* Company Information */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6 border border-blue-200/50 dark:border-blue-600/30">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-6">
                  <FiBriefcase className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  {t('profile.companyInformation')}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">{t('profile.company')}</label>
                    <div className="p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl text-gray-800 dark:text-gray-100">
                      {editedProfile.companyName}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">Company Type</label>
                    <div className="p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl text-gray-800 dark:text-gray-100">
                      {editedProfile.companyType}
                    </div>
                  </div>
                  {/* Removed position and location */}
                </div>
              </div>

              {/* Account Information */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-6 border border-green-200/50 dark:border-green-600/30">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-6">
                  <FiCalendar className="w-5 h-5 text-green-600 dark:text-green-400" />
                  {t('profile.accountInformation')}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">Join Date / Trial Start</label>
                    <div className="p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl text-gray-800 dark:text-gray-100">
                      {editedProfile.joinDate ? new Date(editedProfile.joinDate).toLocaleDateString('en-US', {
                        year: 'numeric', month: 'long', day: 'numeric'
                      }) : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">Subscription Status</label>
                    <div className="p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl text-gray-800 dark:text-gray-100">
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                        user?.subscriptionStatus === 'trial'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      }`}>
                        {user?.subscriptionStatus === 'trial' ? t('options.trial') : t('options.proUser')}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">Role</label>
                    <div className="p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl text-gray-800 dark:text-gray-100">
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                        user?.role === 'admin' || user?.role === 'super_admin'
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                      }`}>
                        {user?.role === 'admin' || user?.role === 'super_admin' ? 'Administrator' : 'User'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">Timezone</label>
                    <div className="p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl text-gray-800 dark:text-gray-100">
                      {editedProfile.timezone}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Settings Sidebar */}
            <div className="space-y-6">
              
              {/* Security Section */}
              <div className="bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 rounded-2xl p-6 border border-red-200/50 dark:border-red-600/30">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-4">
                  <FiLock className="w-5 h-5 text-red-600 dark:text-red-400" />
                  Security
                </h3>

                <button
                  onClick={() => setShowPasswordSection(!showPasswordSection)}
                  className="w-full p-3 card-primary text-left text-gray-800 dark:text-gray-100 hover:bg-white dark:hover:bg-gray-800 transition-colors duration-200 flex items-center justify-between"
                >
                  <span>Change Password</span>
                  <FiLock className="w-4 h-4" />
                </button>

                {showPasswordSection && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">Current Password</label>
                      <input
                        type="password"
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                        className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500/50"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">New Password</label>
                      <input
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                        className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500/50"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">Confirm New Password</label>
                      <input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                        className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500/50"
                      />
                    </div>
                    <button
                      onClick={handlePasswordChange}
                      disabled={isChangingPassword}
                      className={`w-full btn-danger ${
                        isChangingPassword 
                          ? "bg-gray-400 cursor-not-allowed" 
                          : ""
                      }`}
                    >
                      {isChangingPassword ? "Updating Password..." : "Update Password"}
                    </button>
                    {passwordError && (
                      <p className="text-red-500 text-sm mt-2">{passwordError}</p>
                    )}
                    {passwordSuccess && (
                      <p className="text-green-500 text-sm mt-2">{passwordSuccess}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Quick Stats */}
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-2xl p-6 border border-purple-200/50 dark:border-purple-600/30">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-4">
                  <FiSettings className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  Quick Stats
                </h3>

                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Documents Uploaded</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-100">
                      {isLoadingStats ? "Loading..." : documentCount}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Sessions Created</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-100">
                      {isLoadingStats ? "Loading..." : sessionCount}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Total Questions</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-100">
                      {isLoadingStats ? "Loading..." : totalChats}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Storage Used (est.)</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-100">
                      {isLoadingStats ? "Loading..." : storageUsed}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Last Login</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-100">
                      {isLoadingStats ? "Loading..." : lastLogin}
                    </span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </AnimatedModal>
  );
}

export default SettingsModal;
